import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Home, MapPin, Play } from 'lucide-react';
import { EmbedFrame, canHover, formatDuration, isVideo } from './embed';
import { PLATFORM_LABEL, dealLabel, isSale, postedAgo, priceLabel, typeLabel } from './labels';
import { CopyButton, ShareButton, WhatsAppButton } from './share';
import { track } from './track';

const shortDate = (iso) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} · ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
};

function mediaBadge(l) {
  if (isVideo(l)) return l.duration_s ? formatDuration(l.duration_s) : 'Vidéo';
  if (l.gallery.length) return `${l.gallery.length} photos`;
  return 'Photo';
}

// Cover image; while hovered, a video plays muted in place and a multi-photo post cross-fades
// through its photos. Clicking opens the player.
function Thumb({ listing, hoverPlay, onOpen, price }) {
  const [active, setActive] = useState(false);
  const [slide, setSlide] = useState(0);
  const timer = useRef(null);
  const gallery = listing.gallery;
  const hoverable = canHover && hoverPlay && (isVideo(listing) || gallery.length > 1);

  useEffect(() => {
    if (!active || gallery.length < 2) return undefined;
    const t = setInterval(() => setSlide((k) => (k + 1) % gallery.length), 1100);
    return () => clearInterval(t);
  }, [active, gallery.length]);

  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => { if (!hoverPlay) setActive(false); }, [hoverPlay]);

  const enter = () => {
    if (!hoverable) return;
    timer.current = setTimeout(() => setActive(true), isVideo(listing) ? 350 : 150);
  };
  const leave = () => {
    clearTimeout(timer.current);
    setActive(false);
    setSlide(0);
  };
  const click = (e) => {
    if (e.metaKey || e.ctrlKey) { track('contact', listing); return; }
    e.preventDefault();
    leave();
    track('play', listing);
    onOpen(listing);
  };

  const playingVideo = active && isVideo(listing);
  return (
    <a
      href={listing.url}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={enter}
      onMouseLeave={leave}
      onClick={click}
      aria-label="Lire l'annonce"
      className={`group relative block aspect-[4/5] overflow-hidden ${playingVideo ? 'bg-black' : 'bg-gray-100'}`}
    >
      {playingVideo ? (
        <EmbedFrame listing={listing} muted />
      ) : (
        <>
          {active && gallery.length > 1 ? (
            gallery.map((src, k) => (
              <img
                key={src}
                src={src}
                alt=""
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${k === slide ? 'opacity-100' : 'opacity-0'}`}
              />
            ))
          ) : listing.thumbnail_url ? (
            <img
              src={listing.thumbnail_url}
              alt={typeLabel(listing) || 'Logement'}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-gray-300"><Home size={48} /></div>
          )}

          {isVideo(listing) && (
            <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="bg-black/55 text-white rounded-full p-3"><Play size={20} fill="currentColor" /></span>
            </span>
          )}
          {active && gallery.length > 1 && (
            <span className="absolute left-0 right-0 bottom-3 flex justify-center gap-1.5 pointer-events-none">
              {gallery.map((src, k) => (
                <i key={src} className={`w-1.5 h-1.5 rounded-full shadow ${k === slide ? 'bg-white' : 'bg-white/50'}`} />
              ))}
            </span>
          )}
          {price && (
            <span className="absolute top-2 left-2 bg-white text-blue-700 font-bold text-sm px-2.5 py-1 rounded-full shadow">{price}</span>
          )}
          <span className="absolute top-2 right-2 bg-white/90 text-gray-700 text-xs px-2 py-1 rounded-full">
            {postedAgo(listing.posted_at)}
          </span>
          <span className={`absolute bottom-2 left-2 text-xs font-bold px-2 py-1 rounded-full shadow ${isSale(listing) ? 'bg-orange-500 text-white' : 'bg-green-600 text-white'}`}>
            {dealLabel(listing)}
          </span>
          <span className="absolute bottom-2 right-2 bg-black/65 text-white text-xs font-semibold px-2 py-1 rounded-full">
            {mediaBadge(listing)}
          </span>
        </>
      )}
    </a>
  );
}

const ListingCard = ({ listing, hoverPlay, onOpen }) => {
  const [expanded, setExpanded] = useState(false);
  const price = priceLabel(listing);
  const type = typeLabel(listing);
  const place = [listing.area, listing.city].filter(Boolean).join(' · ');
  const platform = PLATFORM_LABEL[listing.platform];
  const copies = listing.also_posted;
  const longCaption = (listing.caption || '').length > 140;

  return (
    <article className="bg-white text-gray-900 rounded-xl shadow-sm overflow-hidden flex flex-col">
      <Thumb listing={listing} hoverPlay={hoverPlay} onOpen={onOpen} price={price} />

      <div className="p-4 flex flex-col gap-1.5 flex-grow">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`text-[11px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${listing.platform === 'tiktok' ? 'text-gray-900 border-gray-900' : 'text-blue-700 border-blue-700'}`}>
            {platform}
          </span>
          {listing.agent_url ? (
            <a href={listing.agent_url} target="_blank" rel="noopener noreferrer" onClick={() => track('agent_profile', listing)} className="font-semibold text-gray-900 truncate hover:underline" title={listing.account}>
              {listing.account}
            </a>
          ) : (
            <span className="font-semibold text-gray-900 truncate">{listing.account}</span>
          )}
          <time dateTime={listing.posted_at} className="ml-auto shrink-0 text-xs text-gray-500 tabular-nums" title={new Date(listing.posted_at).toLocaleString('fr-FR')}>
            {shortDate(listing.posted_at)}
          </time>
        </div>

        <Link to={listing.path} className="flex flex-col gap-1.5 hover:opacity-80">
          {!price && <p className="text-gray-400 text-sm">Prix non indiqué</p>}
          {type && <p className="text-sm font-medium text-blue-700">{type}{listing.furnished === 'yes' ? ' · meublé' : ''}</p>}
          {place && (
            <p className="text-sm text-gray-600 flex items-center gap-1"><MapPin size={14} className="shrink-0" />{place}</p>
          )}
        </Link>
        {listing.new_build && (
          <span className="self-start text-xs font-medium bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">Nouvelle construction</span>
        )}
        {copies.length > 0 && (
          <span
            className="self-start text-xs font-medium bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full"
            title="La même annonce, mot pour mot, publiée plusieurs fois"
          >
            Publiée {copies.length + 1}×
          </span>
        )}

        {listing.caption && (
          <div>
            <Link to={listing.path} className={`text-sm text-gray-500 whitespace-pre-line break-words hover:text-gray-700 ${expanded ? 'block' : 'line-clamp-3'}`}>
              {listing.caption}
            </Link>
            {longCaption && (
              <button type="button" onClick={() => setExpanded(!expanded)} className="text-xs text-gray-500 underline mt-1">
                {expanded ? 'Voir moins' : 'Voir plus'}
              </button>
            )}
          </div>
        )}

        {copies.length > 0 && (
          <div className="border-t border-dashed border-gray-200 pt-2 mt-1 text-xs flex flex-col gap-1">
            <span className="font-semibold uppercase tracking-wide text-gray-400">Aussi publiée</span>
            {copies.map((c) => (
              <div key={c.url} className="flex items-center gap-2">
                <a href={c.url} target="_blank" rel="noopener noreferrer" onClick={() => track('contact', listing)} className="text-gray-700 hover:text-blue-700 min-w-0 truncate">
                  {PLATFORM_LABEL[c.platform]} · {c.account} <span className="text-gray-400">{shortDate(c.posted_at)} ↗</span>
                </a>
                <CopyButton text={c.url} variant="icon-sm" label="" title={`Copier le lien ${PLATFORM_LABEL[c.platform]}`} onCopy={() => track('copy_source', listing)} />
              </div>
            ))}
          </div>
        )}

        <div className="mt-auto pt-3 flex flex-col gap-2">
          <WhatsAppButton listing={listing} />
          <div className="flex gap-2">
            <a
              href={listing.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track('contact', listing)}
              className={`flex-1 min-w-0 flex items-center justify-center gap-2 ${listing.whatsapp_url
                ? 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium px-3 py-2 rounded-lg'
                : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-medium px-3 py-2 rounded-lg hover:opacity-90'}`}
            >
              <span className="truncate">Voir sur {platform}</span> <ExternalLink size={14} className="shrink-0" />
            </a>
            <ShareButton listing={listing} variant="icon" />
            <CopyButton text={listing.url} variant="icon" label="" title={`Copier le lien ${platform}`} onCopy={() => track('copy_source', listing)} />
          </div>
        </div>
      </div>
    </article>
  );
};

export default ListingCard;
